import {
  Check,
  Lightbulb,
  Plus,
  RefreshCw,
  Trash2,
  X
} from 'lucide-react';
import { FaSave } from "react-icons/fa";
import { useEffect, useMemo, useState } from 'react';
import { useDomainesCached } from '../hooks_back/useDomainesCached';
import { useDomainSuggestions } from '../hooks_suggestion/useDomainSuggestions';
import colors from "../../../../../styles/colors_global";

export const DomainsModal = ({
  isOpen,
  onClose,
  onDomainsUpdated,
  customSkillDomains: legacyCustomSkillDomains = [],
  setCustomSkillDomains: setLegacyCustomSkillDomains = () => { },
}) => {
  const {
    domaines,
    loadingDomaines,
    errorDomaines,
    createDomaine,
    deleteDomaine,
    refetchDomaines,
    isFromCache,
    invalidateCache
  } = useDomainesCached({
    cacheDuration: 10 * 60 * 1000,
    forceRefresh: false,
    autoRefresh: true
  });

  const { domainSuggestions, loading: loadingSuggestions, error: errorSuggestions } = useDomainSuggestions();
  const [tempDomainsAdded, setTempDomainsAdded] = useState([]);
  const [tempDomainsRemovedIds, setTempDomainsRemovedIds] = useState(new Set());
  const [newSkillDomainInput, setNewSkillDomainInput] = useState('');
  const [isSavingChanges, setIsSavingChanges] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setTempDomainsAdded([]);
      setTempDomainsRemovedIds(new Set());
      setNewSkillDomainInput('');
    }
  }, [isOpen]);

  const addCustomSkillDomainTemp = () => {
    const domainName = newSkillDomainInput.trim();
    if (!domainName) return;

    const allExistingDomainsForCheck = [
      ...domaines,
      ...tempDomainsAdded,
      ...legacyCustomSkillDomains.map(name => ({ nom_domaine: name }))
    ].filter(d => !tempDomainsRemovedIds.has(d.id_domaine));

    const domainExists = allExistingDomainsForCheck.some(d =>
      (d.nom_domaine || d.name)?.toLowerCase() === domainName.toLowerCase()
    );

    if (domainExists) {
      alert('Ce domaine existe déjà dans votre liste ou est en attente d\'ajout/suppression !');
      return;
    }

    const domainInDb = domaines.find(d => d.nom_domaine.toLowerCase() === domainName.toLowerCase());
    if (domainInDb && tempDomainsRemovedIds.has(domainInDb.id_domaine)) {
      setTempDomainsRemovedIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(domainInDb.id_domaine);
        return newSet;
      });
      setNewSkillDomainInput('');
      return;
    }

    setTempDomainsAdded(prev => [
      ...prev,
      {
        nom_domaine: domainName,
        description: `Domaine personnalisé : ${domainName}`,
        icon: '🎯',
        type_domaine: 'custom',
        score_evaluation: 0,
        niveau_evaluation: 'Débutant',
        id: `temp-${Date.now()}-${Math.random()}`
      }
    ]);
    setNewSkillDomainInput('');
  };

  const removeDomainTemp = (domainToRemove) => {
    if (domainToRemove.id && domainToRemove.id.startsWith('temp-')) {
      setTempDomainsAdded(prev => prev.filter(d => d.id !== domainToRemove.id));
    }
    else if (domainToRemove.id_domaine) {
      setTempDomainsRemovedIds(prev => new Set(prev).add(domainToRemove.id_domaine));
    }
    else if (domainToRemove.type_domaine === 'legacy_custom' || domainToRemove.type_domaine === 'legacy_suggestion') {
      setLegacyCustomSkillDomains(prev => prev.filter(name => name !== domainToRemove.nom_domaine));
    }
  };

  const addSuggestedSkillDomainTemp = (suggestionName) => {
    const allExistingDomainsForCheck = [
      ...domaines,
      ...tempDomainsAdded,
      ...legacyCustomSkillDomains.map(name => ({ nom_domaine: name }))
    ].filter(d => !tempDomainsRemovedIds.has(d.id_domaine));

    const domainExists = allExistingDomainsForCheck.some(d =>
      (d.nom_domaine || d.name)?.toLowerCase() === suggestionName.toLowerCase()
    );

    if (domainExists) return;

    const domainInDb = domaines.find(d => d.nom_domaine.toLowerCase() === suggestionName.toLowerCase());
    if (domainInDb && tempDomainsRemovedIds.has(domainInDb.id_domaine)) {
      setTempDomainsRemovedIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(domainInDb.id_domaine);
        return newSet;
      });
      return;
    }

    setTempDomainsAdded(prev => [
      ...prev,
      {
        nom_domaine: suggestionName,
        description: `Domaine suggéré : ${suggestionName}`,
        icon: '💡',
        type_domaine: 'predefined',
        score_evaluation: 0,
        niveau_evaluation: 'Débutant',
        id: `temp-${Date.now()}-${Math.random()}`
      }
    ]);
  };

  // Dans DomainsModal.js - handleSaveChanges modifié

const handleSaveChanges = async () => {
  setIsSavingChanges(true);
  let allSucceeded = true;
  const errors = [];

  try {
    for (const domainId of Array.from(tempDomainsRemovedIds)) {
      try {
        const result = await deleteDomaine(domainId);
        if (!result.success) {
          errors.push(`Erreur suppression domaine ${domainId}: ${result.error}`);
          allSucceeded = false;
        }
      } catch (error) {
        errors.push(`Exception suppression domaine ${domainId}: ${error.message}`);
        allSucceeded = false;
      }
    }

    for (const domainData of tempDomainsAdded) {
      try {
        const result = await createDomaine({
          nom_domaine: domainData.nom_domaine,
          description: domainData.description,
          icon: domainData.icon,
          type_domaine: domainData.type_domaine,
          score_evaluation: domainData.score_evaluation,
          niveau_evaluation: domainData.niveau_evaluation
        });
        if (!result.success) {
          errors.push(`Erreur création domaine ${domainData.nom_domaine}: ${result.error}`);
          allSucceeded = false;
        }
      } catch (error) {
        errors.push(`Exception création domaine ${domainData.nom_domaine}: ${error.message}`);
        allSucceeded = false;
      }
    }

    // SOLUTION SIMPLE: Rafraîchir la page complète
    window.location.reload();

  } catch (error) {
    alert(`Erreur critique: ${error.message}`);
  } finally {
    setIsSavingChanges(false);
    setTempDomainsAdded([]);
    setTempDomainsRemovedIds(new Set());
    setNewSkillDomainInput('');
    onClose();
  }
};

  const handleForceRefresh = async () => {
    invalidateCache();
    await refetchDomaines();
  };

  const domainsWithPendingChanges = useMemo(() => {
    const changes = [];
    tempDomainsAdded.forEach(domain => {
      changes.push({ ...domain, isNew: true, isPendingRemoval: false });
    });
    domaines.forEach(domain => {
      if (tempDomainsRemovedIds.has(domain.id_domaine)) {
        changes.push({ ...domain, isNew: false, isPendingRemoval: true });
      }
    });
    return changes;
  }, [domaines, tempDomainsAdded, tempDomainsRemovedIds]);

  const availableSuggestions = useMemo(() => {
    const allActiveNames = new Set();
    domaines.forEach(d => {
      if (!tempDomainsRemovedIds.has(d.id_domaine)) {
        allActiveNames.add(d.nom_domaine.toLowerCase());
      }
    });
    tempDomainsAdded.forEach(d => allActiveNames.add(d.nom_domaine.toLowerCase()));
    legacyCustomSkillDomains.forEach(name => allActiveNames.add(name.toLowerCase()));
    return domainSuggestions.filter(suggestion => !allActiveNames.has(suggestion.toLowerCase()));
  }, [domainSuggestions, domaines, tempDomainsAdded, tempDomainsRemovedIds, legacyCustomSkillDomains]);

  const totalActiveDomainsCount = useMemo(() => {
    const dbActiveCount = domaines.filter(d => !tempDomainsRemovedIds.has(d.id_domaine)).length;
    const tempAddedCount = tempDomainsAdded.length;
    return dbActiveCount + tempAddedCount;
  }, [domaines, tempDomainsAdded, tempDomainsRemovedIds]);

  const hasChanged = tempDomainsAdded.length > 0 || tempDomainsRemovedIds.size > 0;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        <div className="bg-gray-100 border-b border-gray-200 px-8 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <h2 className="text-2xl font-medium text-gray-900 tracking-tight">Domaines d'Expertise</h2>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={handleForceRefresh}
                disabled={loadingDomaines || isSavingChanges}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
                title="Actualiser les domaines (invalide le cache)"
              >
                <RefreshCw className={`w-4 h-4 ${loadingDomaines ? 'animate-spin' : ''}`} />
              </button>
              <button
                onClick={onClose}
                disabled={isSavingChanges}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        <div className="p-8 max-h-[calc(90vh-120px)] overflow-y-auto">
          {errorDomaines && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">
                Erreur lors de la gestion des domaines: {errorDomaines.message}
              </p>
            </div>
          )}

          <div className="mb-6">
            <h3 className="text-xl font-medium text-gray-900 mb-4 tracking-tight">
              Ajouter un Domaine Personnalisé
            </h3>
            <div className="flex space-x-3">
              <input
                type="text"
                value={newSkillDomainInput}
                onChange={(e) => setNewSkillDomainInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && !isSavingChanges && addCustomSkillDomainTemp()}
                placeholder="Ex: Trading d'options, Analyse comportementale..."
                className="flex-1 px-4 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#18574F] focus:border-transparent text-sm"
                disabled={isSavingChanges}
              />
              <button
                onClick={addCustomSkillDomainTemp}
                disabled={!newSkillDomainInput.trim() || isSavingChanges}
                className="px-6 py-1.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center border border-gray-200 font-medium"
              >
                <Plus className="w-4 h-4 mr-2" />
                Ajouter
              </button>
            </div>
          </div>

          {domainsWithPendingChanges.length > 0 && (
            <div className="mb-6">
              <h3 className="text-xl font-medium text-gray-900 mb-4 tracking-tight">
                Modifications en Attente
                <span className="ml-2 text-sm text-gray-500 font-normal">
                  ({domainsWithPendingChanges.length})
                </span>
              </h3>
              <div className="space-y-2">
                {domainsWithPendingChanges.map((domain) => {
                  const domainName = domain.nom_domaine || domain.name;
                  const isPendingRemoval = domain.isPendingRemoval;
                  const isNewTemp = domain.isNew;

                  return (
                    <div key={domain.id || domain.id_domaine} className={`flex items-center justify-between p-2 rounded-lg group
                      ${isPendingRemoval ? 'bg-red-50 border border-red-200 opacity-60 line-through' :
                        isNewTemp ? 'bg-[#c8d7d6]' : ''}`}>
                      <div className="flex items-center space-x-3">
                        {!isPendingRemoval && (
                          <RefreshCw className="w-4 h-4 text-teal-700 flex-shrink-0" />
                        )}
                        <div>
                          <span className="text-gray-900 text-sm">{domainName}</span>
                          {isPendingRemoval && (
                            <div className="mt-1">
                              <span className="text-xs px-2 py-1 rounded-full bg-red-100 text-red-700">
                                En attente de suppression
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                      {isPendingRemoval ? (
                        <button
                          onClick={() => {
                            setTempDomainsRemovedIds(prev => {
                              const newSet = new Set(prev);
                              newSet.delete(domain.id_domaine);
                              return newSet;
                            });
                          }}
                          className="px-3 py-1 bg-gray-200 text-gray-700 rounded text-xs hover:bg-gray-300 transition-all flex items-center"
                          disabled={isSavingChanges}
                        >
                          <X className="w-3 h-3 mr-1" />Annuler
                        </button>
                      ) : (
                        <button
                          onClick={() => removeDomainTemp(domain)}
                          className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 transition-all p-1 hover:bg-red-100 rounded"
                          disabled={isSavingChanges}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div>
            <h3 className="text-xl font-medium text-gray-900 mb-4 tracking-tight">
              Domaines Suggérés
              {loadingSuggestions && (
                <span className="ml-2 text-sm text-gray-500 font-normal">(Chargement...)</span>
              )}
            </h3>
            {errorSuggestions && (
              <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                <p className="text-orange-600 text-sm">
                  Erreur lors du chargement des suggestions. Utilisation des suggestions par défaut.
                </p>
              </div>
            )}
            <div className="grid grid-cols-1 gap-2">
              {availableSuggestions.map((suggestion, index) => (
                <div key={index} className="flex items-center justify-between p-2 bg-[#fff3e6] rounded-lg hover:bg-[#ffad65] transition-colors group cursor-pointer border-2 border-dashed border-orange-300 hover:border-orange-400"
                  onClick={() => addSuggestedSkillDomainTemp(suggestion)}>
                  <div className="flex items-center space-x-3">
                    <Lightbulb className="w-4 h-4 text-orange-600 flex-shrink-0" />
                    <span className="text-gray-900 text-sm">{suggestion}</span>
                  </div>
                  <Plus className="w-4 h-4 text-orange-500 group-hover:text-orange-700 group-hover:scale-110 transition-all" />
                </div>
              ))}
            </div>
            {!loadingSuggestions && availableSuggestions.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <Lightbulb className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>Toutes les suggestions ont été ajoutées !</p>
              </div>
            )}
          </div>
        </div>

        <div className="bg-white px-8 py-2 border-t border-gray-200">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-600 font-medium">
              {(tempDomainsAdded.length > 0 || tempDomainsRemovedIds.size > 0) ? (
                <span>
                  {tempDomainsAdded.length} ajout{tempDomainsAdded.length > 1 ? 's' : ''} • {' '}
                  {tempDomainsRemovedIds.size} suppression{tempDomainsRemovedIds.size > 1 ? 's' : ''}
                </span>
              ) : (
                <span>Aucun changement en attente</span>
              )}
            </div>
            <div className="flex space-x-3">
              <button
                onClick={onClose}
                disabled={isSavingChanges}
                className="px-6 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-50 rounded-lg transition-colors font-medium"
              >
                Annuler
              </button>
              <button
                onClick={handleSaveChanges}
                disabled={!hasChanged || isSavingChanges}
                className="px-8 py-2 rounded-lg text-sm flex items-center justify-center font-medium"
                style={{
                  backgroundColor: hasChanged ? colors.green : colors.lightGreen,
                  color: "white",
                }}
                onMouseEnter={(e) => {
                  if (!e.target.disabled && hasChanged) {
                    e.target.style.backgroundColor = colors.green;
                  }
                }}
                onMouseLeave={(e) => {
                  if (!e.target.disabled) {
                    e.target.style.backgroundColor = hasChanged ? colors.green : colors.lightGreen;
                  }
                }}
              >
                <FaSave className="mr-2" />
                {isSavingChanges ? "Enregistrement..." : "Enregistrer"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};